import React from 'react';
import { Box } from '@mui/material';
import { ColumnFlexBox } from '../../FlexBox';

/** How near an edge a carried line must be for the list to start scrolling, in pixels. */
const SCROLL_EDGE = 48;

/** How fast the list scrolls when a carried line is right against an edge, in pixels a frame. */
const SCROLL_SPEED = 10;

/** The list with the item at `from` taken out and put back in at `to`. */
export const reordered = (items, from, to) => {
  if (from === null || from === to) return items;

  const next = [...items];
  const [moved] = next.splice(from, 1);

  next.splice(to, 0, moved);

  return next;
};

/**
 * A list whose order is changed by dragging its lines up and down it.
 *
 * The order of a layout's entries is a column, so the only place a line can go is above or below
 * another one; the line being passed shows a rule where the carried one would land. Dropping takes
 * it out and puts it back in there rather than swapping it with its neighbour, so moving something
 * five places is one drag.
 *
 * A line carried past either end scrolls the list, faster the further past it is - the order being
 * dropped into is longer than the room there is to show it.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.items - What is listed, in order.
 * @param {Function} props.keyOf - What names an item, for React and for the drag.
 * @param {Function} props.onReorder - Receives `(from, to)`.
 * @param {Function} props.children - Draws one item, as `(item, index)`.
 *
 * @returns {React.JSX.Element}
 */
const SortableRows = function SortableRows({
  items = [],
  keyOf = (item) => item.key,
  onReorder = () => {},
  children,
}) {
  const carried = React.useRef(null);
  const [over, setOver] = React.useState(null);
  // The list, and how fast it is scrolling itself while a line is carried against one of its ends.
  const list = React.useRef(null);
  const speed = React.useRef(0);
  const frame = React.useRef(null);

  const stopScrolling = React.useCallback(() => {
    speed.current = 0;

    if (frame.current !== null) cancelAnimationFrame(frame.current);

    frame.current = null;
  }, []);

  const keepScrolling = React.useCallback(() => {
    if (!list.current || !speed.current) {
      frame.current = null;
      return;
    }

    list.current.scrollTop += speed.current;
    frame.current = requestAnimationFrame(keepScrolling);
  }, []);

  const scrollTowards = (y) => {
    if (!list.current) return;

    const { top, bottom } = list.current.getBoundingClientRect();
    const above = (SCROLL_EDGE - (y - top)) / SCROLL_EDGE;
    const below = (SCROLL_EDGE - (bottom - y)) / SCROLL_EDGE;

    speed.current =
      above > 0
        ? -SCROLL_SPEED * Math.min(above, 1)
        : below > 0
          ? SCROLL_SPEED * Math.min(below, 1)
          : 0;

    if (speed.current && frame.current === null)
      frame.current = requestAnimationFrame(keepScrolling);
  };

  React.useEffect(() => stopScrolling, [stopScrolling]);

  return (
    <ColumnFlexBox
      ref={list}
      gap="8px"
      // Every line's dragging reaches the list, so the scrolling is answered once here rather than
      // by each of them.
      onDragOver={(e) => scrollTowards(e.clientY)}
      onDragEnd={stopScrolling}
      onDrop={stopScrolling}
      // Crossing from the list into one of its lines is a leaving too, and stopping on that would
      // stop on the first line reached.
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) stopScrolling();
      }}
      // A field's floating label is drawn above its border, which the scroll box would otherwise
      // cut off the first row.
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: '4px', pt: '8px' }}
    >
      {items.map((item, index) => (
        <Box
          key={keyOf(item)}
          draggable
          onDragStart={(e) => {
            carried.current = index;
            e.dataTransfer.effectAllowed = 'move';
            // Carried for the browser's sake: a drag with nothing on it is not a drag.
            e.dataTransfer.setData('text/plain', String(keyOf(item)));
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(index);
          }}
          onDragEnd={() => {
            carried.current = null;
            setOver(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            onReorder(carried.current, index);
            carried.current = null;
            setOver(null);
          }}
          sx={{
            cursor: 'grab',
            borderTop: '2px solid',
            borderColor:
              over === index && carried.current !== index
                ? 'primary.main'
                : 'transparent',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          {children(item, index)}
        </Box>
      ))}
    </ColumnFlexBox>
  );
};

export default SortableRows;
